import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { FileText, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from "lucide-react";

interface PolicyProps {
  propertyData: PropertyToEdit | null;
}

const Policy: React.FC<PolicyProps> = ({ propertyData }) => {
  const [expanded, setExpanded] = useState(true);

  const rules = propertyData?.additional_rules ?? [];
  const hasRules = rules.length > 0;

  // Show first 4 rules collapsed, rest on expand
  const PREVIEW_COUNT = 4;
  const visibleRules = expanded ? rules : rules.slice(0, PREVIEW_COUNT);
  const hiddenCount = rules.length - PREVIEW_COUNT;

  return (
    <div className="listingSection__wrap w-full space-y-6">

      {/* Section header */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <span className="block w-8 h-0.5 bg-[#F56948] rounded-full" />
          <span className="text-xs font-semibold uppercase tracking-widest text-[#F56948]">
            Rules
          </span>
        </div>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-extrabold text-[#1A1A2E] dark:text-white tracking-tight">
            Property Rules
          </h2>
          {hasRules && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#F56948]/10 text-[#F56948] border border-[#F56948]/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F56948]" />
              {rules.length} {rules.length === 1 ? "rule" : "rules"}
            </span>
          )}
        </div>
      </div>

      <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

      {hasRules ? (
        <div className="space-y-4">

          {/* Two-column rules grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {visibleRules.map((rule, index) => (
              <div
                key={rule.id}
                className="group flex items-start gap-3 p-3.5 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-100 dark:border-neutral-800 hover:border-[#F56948]/30 hover:bg-[#F56948]/5 dark:hover:bg-[#F56948]/5 transition-all duration-150"
              >
                {/* Index number */}
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-[#F56948]/10 text-[#F56948] font-bold text-[11px] group-hover:bg-[#F56948] group-hover:text-white transition-colors duration-150">
                  {index + 1}
                </div>

                {/* Rule text */}
                <p className="flex-1 text-sm text-neutral-600 dark:text-neutral-400 leading-snug pt-0.5">
                  {rule.name}
                </p>

                {/* Check */}
                <CheckCircle2
                  size={15}
                  className="flex-shrink-0 text-neutral-300 dark:text-neutral-700 group-hover:text-[#F56948] transition-colors duration-150 mt-0.5"
                />
              </div>
            ))}
          </div>

          {/* Show more / less toggle */}
          {rules.length > PREVIEW_COUNT && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold border-2 border-dashed border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500 hover:border-[#F56948]/40 hover:text-[#F56948] transition-all duration-150"
            >
              {expanded ? (
                <>
                  <ChevronUp size={14} />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Show {hiddenCount} more {hiddenCount === 1 ? "rule" : "rules"}
                </>
              )}
            </button>
          )}

          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40">
            <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-800 dark:text-amber-200 mb-0.5">
                Please review carefully
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                By booking this property you agree to follow all rules listed above.
                Failure to comply may result in booking cancellation.
              </p>
            </div>
          </div>

        </div>
      ) : (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
          <div className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            <FileText size={22} className="text-neutral-400 dark:text-neutral-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A1A2E] dark:text-white">No additional rules</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-xs leading-relaxed">
              This property follows standard house rules. Refer to the cancellation policy section for more details.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Policy;